import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Paperclip } from '@phosphor-icons/react';

const CHECK_COLORS = { pending: 'bg-slate-100 text-slate-500', verified: 'bg-green-100 text-green-800', failed: 'bg-rose-100 text-rose-700' };
const CASE_COLORS = { in_progress: 'bg-amber-100 text-amber-800', pending_decision: 'bg-blue-100 text-blue-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-rose-100 text-rose-700' };

export default function Bgv() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ['global_admin', 'school_admin', 'principal'].includes(user?.role);
  const { data: cases } = useQuery({ queryKey: ['bgv-cases'], queryFn: () => api.get('/bgv/cases').then((r) => r.data) });
  const { data: plugin } = useQuery({ queryKey: ['bgv-plugin'], queryFn: () => api.get('/bgv/plugin').then((r) => r.data), enabled: isAdmin });
  const { data: drivers } = useQuery({ queryKey: ['t-drivers'], queryFn: () => api.get('/transport/drivers').then((r) => r.data).catch(() => []) });
  const { data: staff } = useQuery({ queryKey: ['staff-users'], queryFn: () => api.get('/users').then((r) => r.data.filter((u) => !['student', 'guardian'].includes(u.role))) });
  const [form, setForm] = useState({ subject_kind: 'staff', subject_id: '', role: '' });
  const [vendorKey, setVendorKey] = useState('');
  const fileRef = useRef(null);
  const uploadCtx = useRef(null);
  const inval = () => qc.invalidateQueries({ queryKey: ['bgv-cases'] });

  const createMut = useMutation({
    mutationFn: () => {
      const pool = form.subject_kind === 'driver' ? drivers : staff;
      const subj = (pool || []).find((x) => x.id === form.subject_id);
      return api.post('/bgv/cases', { tenant_id: user?.tenant_id, subject_kind: form.subject_kind, subject_id: form.subject_id, subject_name: subj?.name || 'Unknown', role: form.role || subj?.role || '' });
    },
    onSuccess: () => { toast.success('BGV case initiated'); setForm({ ...form, subject_id: '' }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const checkMut = useMutation({
    mutationFn: ({ cid, check, status }) => api.post(`/bgv/cases/${cid}/checks/${check}`, { status, note: '' }),
    onSuccess: () => inval(), onError: (e) => toast.error(errMsg(e)),
  });
  const decideMut = useMutation({
    mutationFn: ({ cid, approve }) => api.post(`/bgv/cases/${cid}/decide`, { approve, note: approve ? '' : (window.prompt('Rejection note:') || '') }),
    onSuccess: (r) => { toast.success(`Case ${r.data.status}${r.data.valid_until ? ` - valid until ${r.data.valid_until}` : ''}`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const vendorMut = useMutation({
    mutationFn: (vendor) => api.post('/bgv/plugin', { vendor, api_key: vendorKey || undefined }),
    onSuccess: (r) => { toast.success(`Vendor set: ${r.data.vendor}`); setVendorKey(''); qc.invalidateQueries({ queryKey: ['bgv-plugin'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const submitVendorMut = useMutation({
    mutationFn: (cid) => api.post(`/bgv/cases/${cid}/submit-vendor`),
    onSuccess: (r) => { toast.success(`Sent to ${r.data.vendor}: ${r.data.status}`); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const uploadDoc = async (file) => {
    const { cid, check } = uploadCtx.current || {};
    if (!cid) return;
    const fd = new FormData(); fd.append('check', check); fd.append('file', file);
    try {
      await api.post(`/bgv/cases/${cid}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document attached'); inval();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pool = form.subject_kind === 'driver' ? drivers : staff;
  return (
    <div data-testid="bgv-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">Background Verification</h1>
      <p className="text-sm text-slate-500 mb-6">End-to-end BGV for staff & transport drivers - checks, documents, decisions, renewals</p>
      <input ref={fileRef} type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadDoc(e.target.files[0]); e.target.value = ''; }} />

      {isAdmin && (
        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="bgv-initiate-panel">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Initiate case</p>
            <div className="flex gap-2 flex-wrap">
              <select data-testid="bgv-kind-select" value={form.subject_kind} onChange={(e) => setForm({ ...form, subject_kind: e.target.value, subject_id: '' })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                <option value="staff">Staff / Faculty</option>
                <option value="driver">Transport driver</option>
              </select>
              <select data-testid="bgv-subject-select" value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white flex-1 min-w-[180px]">
                <option value="">Select person...</option>
                {(pool || []).map((p) => <option key={p.id} value={p.id}>{p.name} {p.role ? `(${p.role.replace(/_/g, ' ')})` : ''}</option>)}
              </select>
              <Button data-testid="bgv-initiate" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!form.subject_id || createMut.isPending} onClick={() => createMut.mutate()}>Initiate BGV</Button>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="bgv-plugin-panel">
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Vendor plugin {plugin?.has_key && <span className="text-green-700">· key saved</span>}</p>
            <div className="flex gap-2 flex-wrap items-center">
              {(plugin?.vendors || []).map((v) => (
                <button key={v} data-testid={`bgv-vendor-${v}`} onClick={() => vendorMut.mutate(v)}
                  className={`text-xs border rounded px-2 py-1 ${plugin?.vendor === v ? 'border-orange-500 text-orange-700' : 'border-slate-200'}`}>{v.replace(/_/g, ' ')}</button>
              ))}
              <Input data-testid="bgv-vendor-key" type="password" placeholder="Vendor API key (encrypted)" className="h-8 text-xs w-52" value={vendorKey} onChange={(e) => setVendorKey(e.target.value)} />
            </div>
            <p className="text-[11px] text-slate-400 mt-2">With a vendor + key set, cases can be submitted to the vendor API (stubbed until a live key is used). Manual verification always works.</p>
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="bgv-case-list">
        {(cases || []).map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`bgv-case-${c.id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="font-semibold">{c.subject_name}</span>
              <span className="text-xs text-slate-400">{c.subject_kind}{c.role ? ` · ${c.role.replace(/_/g, ' ')}` : ''}</span>
              <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${CASE_COLORS[c.status] || 'bg-slate-100'}`} data-testid={`bgv-status-${c.id}`}>{c.status.replace(/_/g, ' ')}</span>
              {c.valid_until && <span className="font-mono-label text-[9px] uppercase text-slate-400">valid until {c.valid_until}</span>}
              {c.vendor_submission && <span className="font-mono-label text-[9px] uppercase bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">{c.vendor_submission.vendor}: submitted</span>}
              {isAdmin && ['in_progress', 'pending_decision'].includes(c.status) && (
                <span className="ms-auto flex gap-2">
                  {plugin?.has_key && plugin?.vendor !== 'manual' && (
                    <button data-testid={`bgv-vendor-submit-${c.id}`} onClick={() => submitVendorMut.mutate(c.id)} className="text-xs underline text-blue-700">Send to vendor</button>
                  )}
                  <button data-testid={`bgv-approve-${c.id}`} onClick={() => decideMut.mutate({ cid: c.id, approve: true })} className="text-xs bg-green-600 text-white rounded px-2.5 py-1 hover:bg-green-500">Approve</button>
                  <button data-testid={`bgv-reject-${c.id}`} onClick={() => decideMut.mutate({ cid: c.id, approve: false })} className="text-xs border border-rose-200 text-rose-600 rounded px-2.5 py-1 hover:bg-rose-50">Reject</button>
                </span>
              )}
            </div>
            <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {Object.entries(c.checks || {}).map(([check, v]) => (
                <div key={check} className="border border-slate-100 rounded p-2" data-testid={`bgv-check-${c.id}-${check}`}>
                  <p className="font-mono-label text-[9px] uppercase text-slate-400">{check}</p>
                  <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${CHECK_COLORS[v.status]}`}>{v.status}</span>
                  {['in_progress', 'pending_decision'].includes(c.status) && (
                    <div className="flex gap-1 mt-1.5">
                      <button data-testid={`bgv-verify-${c.id}-${check}`} onClick={() => checkMut.mutate({ cid: c.id, check, status: 'verified' })} className="text-[10px] text-green-700 underline">verify</button>
                      <button data-testid={`bgv-fail-${c.id}-${check}`} onClick={() => checkMut.mutate({ cid: c.id, check, status: 'failed' })} className="text-[10px] text-rose-600 underline">fail</button>
                      <button data-testid={`bgv-doc-${c.id}-${check}`} onClick={() => { uploadCtx.current = { cid: c.id, check }; fileRef.current?.click(); }} className="text-[10px] text-slate-400"><Paperclip size={11} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {(c.documents || []).length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2">
                {c.documents.map((d) => (
                  <a key={d.file_id} href={`${API}/bgv/cases/${c.id}/documents/${d.file_id}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">{d.check}: {d.name}</a>
                ))}
              </div>
            )}
          </div>
        ))}
        {(cases || []).length === 0 && <p className="text-sm text-slate-400">No BGV cases yet.</p>}
      </div>
    </div>
  );
}
